import { useState } from "react";
import { signInWithPopup } from "firebase/auth";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import "bootstrap-icons/font/bootstrap-icons.css";

import { auth, googleProvider } from "../../utils/firebase";
import { loginWithGoogleUser } from "../../apis/loginWithGoogleUser";
import { signup } from "../../apis/signup";
import Input from "../campaign/wizard/Input";
import { getApiErrorDetail } from "../../utils/authErrors";
import { setGoogleAuthSession } from "../../utils/authSession";
import { ANALYTICS_AUTH_METHODS, ANALYTICS_CTAS } from "../../analytics/analytics.constants";
import { trackCtaClicked, trackSignUp } from "../../analytics/analytics";
import resolvePostAuthPath from "../../utils/resolvePostAuthPath";
import { APP_ROUTES } from "../../constants/appRoutes";
import { useAuthModal } from "../../context/AuthModalContext";

export default function RegisterForm({
    redirectPath = null,
    onAuthenticated = null,
    onAuthSuccess = null,
    onSwitchToLogin = null,
    onActivationRequired = null,
}) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { openAuthModal } = useAuthModal();
    const [fullName, setFullName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const completeAuth = async () => {
        if (onAuthSuccess) {
            await onAuthSuccess();
            return;
        }

        if (onAuthenticated) {
            onAuthenticated();
            return;
        }

        const path = await resolvePostAuthPath(redirectPath);
        navigate(path);
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            const result = await signup({
                email,
                password,
                full_name: fullName,
            });
            trackSignUp(ANALYTICS_AUTH_METHODS.EMAIL, { pending_activation: true });
            toast.success(result.message || "Account created. Check your email for your activation code.");
            if (onActivationRequired) {
                onActivationRequired(result.email || email);
            } else {
                navigate(`${APP_ROUTES.ACTIVATE}?email=${encodeURIComponent(result.email || email)}`);
            }
        } catch (err) {
            toast.error(getApiErrorDetail(err));
        } finally {
            setSubmitting(false);
        }
    };

    const handleGoogleLogin = async () => {
        try {
            const res = await signInWithPopup(auth, googleProvider);
            const loginDetails = await loginWithGoogleUser({
                uid: res.user.uid,
                email: res.user.email,
                email_verified: res.user.emailVerified,
                display_name: res.user.displayName,
                is_anonymous: res.user.isAnonymous,
                photo_url: res.user.photoURL,
            });
            setGoogleAuthSession(dispatch, res.user, loginDetails.access_token, {
                isNewUser: true,
            });
            await completeAuth();
        } catch (err) {
            if (err.code === "auth/popup-closed-by-user") return;
            toast.error(getApiErrorDetail(err) || "Unable to sign in with Google. Please try again.");
        }
    };

    return (
        <>
            <form onSubmit={handleRegister}>
                <Input
                    label="Full name"
                    labelIcon="person"
                    type="text"
                    name="full_name"
                    autoComplete="name"
                    placeholder="Enter your full name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                    autoFocus
                    wrapperClassName="mb-4"
                />
                <Input
                    label="Email address"
                    labelIcon="envelope"
                    type="email"
                    name="email"
                    autoComplete="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    wrapperClassName="mb-4"
                />
                <Input
                    label="Password"
                    labelIcon="lock"
                    type="password"
                    name="password"
                    autoComplete="new-password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    wrapperClassName="mb-5"
                />
                <div className="grid gap-3">
                    <button
                        type="submit"
                        className="btn btn-primary-app w-full font-semibold"
                        disabled={submitting}
                    >
                        {submitting ? "Creating account…" : "Register"}
                    </button>
                    <div className="text-center text-sm text-slate-500">Or</div>
                    <button
                        type="button"
                        className="btn btn-secondary-app w-full font-semibold"
                        onClick={handleGoogleLogin}
                    >
                        Sign in with Google <i className="bi bi-google ms-1" aria-hidden />
                    </button>
                </div>
            </form>
            <p className="mt-5 text-center text-sm text-slate-600">
                Already have an account?{" "}
                {onSwitchToLogin ? (
                    <button
                        type="button"
                        className="border-0 bg-transparent p-0 font-semibold text-slate-900 hover:text-slate-700"
                        onClick={() => {
                            trackCtaClicked(ANALYTICS_CTAS.AUTH_LOGIN);
                            onSwitchToLogin?.();
                        }}
                    >
                        Login
                    </button>
                ) : (
                    <button
                        type="button"
                        className="border-0 bg-transparent p-0 font-semibold text-slate-900 hover:text-slate-700"
                        onClick={() => {
                            trackCtaClicked(ANALYTICS_CTAS.AUTH_LOGIN);
                            openAuthModal({ initialTab: "login" });
                        }}
                    >
                        Login
                    </button>
                )}
            </p>
        </>
    );
}
